/**
 * Dates, in the local calendar.
 *
 * Health data belongs to the day the user lived, not to a UTC instant. A run
 * at 11pm on the 4th is the 4th's run even though it is the 5th in UTC. So
 * every `day` in storage is derived from LOCAL time, and never from
 * `toISOString().slice(0, 10)` -- which is the UTC date and silently wrong for
 * anyone west of Greenwich for part of every day.
 */
export type Day = string;

/** Local calendar date as YYYY-MM-DD. */
export function toDay(d: Date = new Date()): Day {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Parse YYYY-MM-DD as local midnight (not UTC midnight). */
export function fromDay(day: Day): Date {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
}

/** ISO-8601 instant with local offset preserved. */
export function nowIso(d: Date = new Date()): string {
  return d.toISOString();
}

export function addDays(day: Day, n: number): Day {
  const d = fromDay(day);
  d.setDate(d.getDate() + n);
  return toDay(d);
}

/** Whole days from `a` to `b`, positive when b is later. */
export function daysBetween(a: Day, b: Day): number {
  const ms = fromDay(b).getTime() - fromDay(a).getTime();
  return Math.round(ms / 86_400_000);
}

/** 0 = Sunday .. 6 = Saturday, in local time. */
export function weekdayOf(day: Day): number {
  return fromDay(day).getDay();
}

/** The last `n` days ending at `end`, oldest first. */
export function lastNDays(n: number, end: Day = toDay()): Day[] {
  const out: Day[] = [];
  for (let i = n - 1; i >= 0; i--) out.push(addDays(end, -i));
  return out;
}

/** "3d", "2h", "just now" -- compact relative age for freshness chips. */
export function relativeAge(iso: string, now: number = Date.now()): string {
  const then = Date.parse(iso);
  if (!Number.isFinite(then)) return '--';
  const sec = Math.max(0, Math.round((now - then) / 1000));
  if (sec < 60) return 'just now';
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.round(min / 60);
  if (hr < 48) return `${hr}h`;
  return `${Math.round(hr / 24)}d`;
}

/** Seconds -> "7h 32m". */
export function formatDuration(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) return '--';
  const h = Math.floor(sec / 3600);
  const m = Math.round((sec % 3600) / 60);
  if (h === 0) return `${m}m`;
  return `${h}h ${String(m).padStart(2, '0')}m`;
}
